import {
  HuddleSignalKind,
  IHuddleIceServer,
  IHuddlePeer,
  IHuddleSignal,
  IHuddleSignalDelivery,
} from '@common/interfaces/huddles'

export interface IHuddleMeshDeps {
  selfPeerId: string
  huddleId: string
  iceServers: IHuddleIceServer[]
  outgoingTrack: MediaStreamTrack
  sendSignal: (signal: IHuddleSignal) => void
  onRemoteStream: (peerId: string, stream: MediaStream) => void
  onPeerGone: (peerId: string) => void
  onConnectionChange?: (peerId: string, state: RTCPeerConnectionState) => void
  createPeerConnection?: (config: RTCConfiguration) => RTCPeerConnection
  now?: () => number
}

interface ILink {
  connection: RTCPeerConnection
  isOfferer: boolean
  /** Candidates that arrived before the remote description they belong to. */
  pendingCandidates: RTCIceCandidateInit[]
  openedAt: number
}

/** A seat list older than a newcomer's offer can omit it; that's not the newcomer leaving. */
export const NEW_LINK_GRACE_MS = 10 * 1000

/** An offer or answer lost in transit never reaches `failed`, so a pair this slow is redialled. */
export const NEGOTIATION_TIMEOUT_MS = 15 * 1000

const STALLED_STATES = new Set<RTCPeerConnectionState>(['new', 'connecting'])

/**
 * Which side of a pair sends the offer: the later joiner, so a newcomer calls
 * everyone already there and two browsers never offer to each other at once.
 */
export const isOffererFor = (self: IHuddlePeer, other: IHuddlePeer): boolean =>
  self.joinedAt === other.joinedAt ? self.peerId > other.peerId : self.joinedAt > other.joinedAt

/**
 * One audio connection to every other browser in the huddle, negotiated over the
 * app socket. The room's seat list (from the server) decides who is connected;
 * signals carry the SDP and ICE candidates between pairs.
 */
export class HuddleMesh {
  private readonly links = new Map<string, ILink>()

  private isClosed = false

  constructor(private readonly deps: IHuddleMeshDeps) {}

  get peerIds(): string[] {
    return [...this.links.keys()]
  }

  /** Connect to everyone seated who isn't connected yet; drop anyone who left. */
  syncPeers(peers: IHuddlePeer[]): void {
    if (this.isClosed) return
    const self = peers.find(({ peerId }) => peerId === this.deps.selfPeerId)
    const others = peers.filter(({ peerId }) => peerId !== this.deps.selfPeerId)
    const seated = new Set(others.map(({ peerId }) => peerId))
    const now = this.now()
    this.links.forEach(({ openedAt }, peerId) => {
      if (!seated.has(peerId) && now - openedAt > NEW_LINK_GRACE_MS) this.drop(peerId)
    })
    if (!self) return
    this.links.forEach((link, peerId) => {
      if (link.isOfferer && this.isStalled(link, now)) this.drop(peerId)
    })
    others.forEach((other) => {
      if (this.links.has(other.peerId) || !isOffererFor(self, other)) return
      void this.offer(other.peerId)
    })
  }

  async handleSignal(signal: IHuddleSignalDelivery): Promise<void> {
    if (this.isClosed || signal.toPeerId !== this.deps.selfPeerId) return
    if (signal.huddleId !== this.deps.huddleId) return
    try {
      if (signal.kind === HuddleSignalKind.Offer) await this.answer(signal)
      else if (signal.kind === HuddleSignalKind.Answer) await this.accept(signal)
      else await this.addCandidate(signal)
    } catch {
      // A broken negotiation leaves the pair unconnected; the offerer redials it.
    }
  }

  private isStalled({ connection, openedAt }: ILink, now: number): boolean {
    return STALLED_STATES.has(connection.connectionState) && now - openedAt > NEGOTIATION_TIMEOUT_MS
  }

  private now(): number {
    return this.deps.now?.() ?? Date.now()
  }

  close(): void {
    this.isClosed = true
    ;[...this.links.keys()].forEach((peerId) => this.drop(peerId))
  }

  private async offer(peerId: string): Promise<void> {
    const link = this.open(peerId, true)
    const description = await link.connection.createOffer()
    await link.connection.setLocalDescription(description)
    this.send(peerId, HuddleSignalKind.Offer, description.sdp ?? '')
  }

  private async answer(signal: IHuddleSignalDelivery): Promise<void> {
    // An offer always starts the pair over: the offerer only sends one to connect or redial.
    this.drop(signal.fromPeerId)
    const link = this.open(signal.fromPeerId, false)
    await link.connection.setRemoteDescription({ type: 'offer', sdp: signal.payload })
    await this.flushCandidates(link)
    const description = await link.connection.createAnswer()
    await link.connection.setLocalDescription(description)
    this.send(signal.fromPeerId, HuddleSignalKind.Answer, description.sdp ?? '')
  }

  private async accept(signal: IHuddleSignalDelivery): Promise<void> {
    const link = this.links.get(signal.fromPeerId)
    if (!link?.isOfferer || link.connection.signalingState !== 'have-local-offer') return
    await link.connection.setRemoteDescription({ type: 'answer', sdp: signal.payload })
    await this.flushCandidates(link)
  }

  private async addCandidate(signal: IHuddleSignalDelivery): Promise<void> {
    const link = this.links.get(signal.fromPeerId)
    if (!link) return
    const candidate = JSON.parse(signal.payload) as RTCIceCandidateInit
    if (link.connection.remoteDescription) await link.connection.addIceCandidate(candidate)
    else link.pendingCandidates.push(candidate)
  }

  private async flushCandidates(link: ILink): Promise<void> {
    const pending = link.pendingCandidates.splice(0)
    for (const candidate of pending) await link.connection.addIceCandidate(candidate)
  }

  private open(peerId: string, isOfferer: boolean): ILink {
    const create =
      this.deps.createPeerConnection ??
      ((config: RTCConfiguration) => new RTCPeerConnection(config))
    const connection = create({ iceServers: this.deps.iceServers })
    const link: ILink = { connection, isOfferer, pendingCandidates: [], openedAt: this.now() }
    this.links.set(peerId, link)

    connection.addTrack(this.deps.outgoingTrack, new MediaStream([this.deps.outgoingTrack]))
    connection.onicecandidate = ({ candidate }) => {
      if (candidate) this.send(peerId, HuddleSignalKind.Candidate, JSON.stringify(candidate))
    }
    connection.ontrack = ({ streams, track }) => {
      this.deps.onRemoteStream(peerId, streams[0] ?? new MediaStream([track]))
    }
    connection.onconnectionstatechange = () => {
      if (this.links.get(peerId) !== link) return
      const state = connection.connectionState
      this.deps.onConnectionChange?.(peerId, state)
      // The offerer redials a pair that failed (a network change, a sleeping laptop).
      if (state === 'failed' && link.isOfferer && !this.isClosed) {
        this.drop(peerId)
        void this.offer(peerId)
      }
    }
    return link
  }

  private drop(peerId: string): void {
    const link = this.links.get(peerId)
    if (!link) return
    this.links.delete(peerId)
    link.connection.onicecandidate = null
    link.connection.ontrack = null
    link.connection.onconnectionstatechange = null
    link.connection.close()
    this.deps.onPeerGone(peerId)
  }

  private send(toPeerId: string, kind: HuddleSignalKind, payload: string): void {
    this.deps.sendSignal({
      huddleId: this.deps.huddleId,
      fromPeerId: this.deps.selfPeerId,
      toPeerId,
      kind,
      payload,
    })
  }
}
